import type { ReactElement } from "react";
import type { AngebotReihe } from "@kundeninfo/api-client";
import { useSofortPopup } from "@kundeninfo/ui";
import { Knopf, Pillenreihe } from "@kundeninfo/ui/me-design";
import { formatDatum } from "../../format.js";
import { ANGEBOT_STATUS_LABEL } from "../../vorgangsstatus.js";
import { SUCCESSION_TEXT as T } from "./texts.js";

/** Der Verweis auf eine andere Angebotsreihe (`OfferSeriesRef`, Festlegung 13) — im Vertrag `AngebotReihe`. */
export type OfferSeriesRef = AngebotReihe;

/** Ein Chip MIT Rahmen — anklickbar (Hausregel „Rahmen anklickbar, Fläche Kennzeichen“): die Pille von me-knopf, mit
 *  Sofort-Popup statt `title`. */
function SeriesChip(p: {
  text: string; series: OfferSeriesRef; disabled: boolean; onOpen: (offerId: string) => void;
}): ReactElement {
  const { series } = p;
  const popup = useSofortPopup<HTMLElement>(
    T.seriesPopup(series.title, formatDatum(series.createdOn), ANGEBOT_STATUS_LABEL[series.status]));
  return (
    <Knopf ref={popup} art="sekundaer" gestalt="pille" deaktiviert={p.disabled}
      onClick={() => p.onOpen(series.offerId)}>{p.text}</Knopf>
  );
}

/**
 * Die Nachfolge im Detailkopf (Spec „Kopieren und Nachfolge“, Dirk 476): „ersetzt ‹Nr›“ am Nachfolger — so zeigt das
 * aktuelle Angebot, dass es frühere gibt — und „ersetzt durch ‹Nr›“ an jeder ersetzten Reihe. Ein Klick springt zur
 * Reihe. Ohne Verweis zeichnet der Baustein nichts.
 */
export function ReplacesChips(p: {
  replaces: OfferSeriesRef | null;
  replacedBy: OfferSeriesRef[];
  onOeffnen: (offerId: string) => void;
  /** Gesperrt wie die übrigen Knöpfe des Details (anderswo offenes Formular, laufende Aktion). */
  disabled?: boolean | undefined;
}): ReactElement | null {
  const disabled = p.disabled === true;
  if (p.replaces === null && p.replacedBy.length === 0) return null;
  return (
    <div className="offer-succession-chips" role="group" aria-label={T.chipsLabel}>
      <Pillenreihe>
        {p.replaces !== null && (
          <SeriesChip text={T.replaces(p.replaces.displayNumber)} series={p.replaces} disabled={disabled}
            onOpen={p.onOeffnen} />
        )}
        {p.replacedBy.map((r) => (
          <SeriesChip key={r.seriesId} text={T.replacedBy(r.displayNumber)} series={r} disabled={disabled}
            onOpen={p.onOeffnen} />
        ))}
      </Pillenreihe>
    </div>
  );
}
